import { makeAutoObservable, runInAction } from 'mobx'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { ChatbotTrigger, IChatbotFilters } from 'entities/Chatbot'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { Segment } from 'entities/Segment/model/Segment'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { SendFromStore } from 'widgets/SendFrom'
import { SearchTypesEnum } from 'widgets/SearchDropdown'

export class TriggerSectionStore {
  runAutomatically = false
  sendFromError = ''
  enrollmentStore: EnrollmentStore
  readonly sendFromStore: SendFromStore
  enrollmentFilters = EnrollmentFilters.Empty()

  constructor(readonly entities: IEntitiesStore, private _trigger: ChatbotTrigger) {
    this.enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () =>
            ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'chatbot',
              skipValues: ['tag'],
            }).then(({ data }) => data),
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this.entities
    )
    this.sendFromStore = new SendFromStore(this.entities)

    this.initFilters()
    makeAutoObservable(this)
  }

  get paramsFilters(): IChatbotFilters {
    return {
      contacts: this.enrollmentFilters.contacts.map((c) => c.id),
      segments: this.enrollmentFilters.segments.map((s) => s.id),
      tags: this.enrollmentFilters.tags.map((t) => t.id),
      advanced: this.enrollmentFilters.filters.map((f) => f.filtersParams),
    }
  }

  initFilters = () => this.enrollmentStore.filterEditorStore.initFilters()

  setSendFromError = (error: string) => {
    this.sendFromError = error
  }

  clearSendFromError = () => {
    this.sendFromError = ''
  }

  setRunAutomatically = (value: boolean) => {
    this.runAutomatically = value
    if (!value) {
      this.enrollmentFilters = EnrollmentFilters.Empty()
      this.setAllowReEntering(false)
    }
  }

  setAllowReEntering = (value: boolean) => {
    this._trigger.setAllowReEntering(value)
    this.enrollmentStore.setIsContactReEnroll(value)
  }

  get allowReEntering() {
    return this._trigger.allowReEntering
  }

  editEnrollment = () => {
    const filters = this.enrollmentFilters.clone()

    this.enrollmentStore.openModal({
      title: 'Enroll contacts',
      allowEmptyCount: true,
      filters,
      action: {
        cancel: () => Promise.resolve(true),
        confirm: async (newFilters) => {
          runInAction(() => {
            this.enrollmentFilters = newFilters
          })
          return true
        },
      },
    })
  }

  init = async (filters: IChatbotFilters | null) => {
    if (!filters) return

    const tagsIds = filters.tags ?? []
    const contactsIds = filters.contacts ?? []
    const segmentsIds = filters.segments ?? []

    const [tags, contacts] = await Promise.all([
      TagsApi.getTagsById(tagsIds).then((tags) => tags.map((tag) => new Tag(tag))),
      this.entities.contactsStore.getByIds(contactsIds),
    ])

    const segments = segmentsIds.reduce<Segment[]>((acc, id) => {
      const item = this.entities.segmentsStore.getItem(id)
      if (item) acc.push(item)
      return acc
    }, [])

    const advanced = filters.advanced?.map(
      (f) => new Segment({ id: -1, organization_id: 0, name: '', filters: f, is_favorite: false })
    )

    runInAction(() => {
      this.enrollmentFilters.sync({ tags, segments, contacts, filters: advanced })
    })
  }
}
